import { apiDelete, apiPost } from '@/lib/api';

export type CallResult = 'ANSWERED' | 'NO_ANSWER';

/** Прозвон в строке списка: последний звонок и когда в последний раз дозвонились / не ответили. */
export interface CallSummary {
  lastAt: string;
  lastResult: CallResult;
  /** Кто звонил последним. */
  lastBy: string;
  answeredAt: string | null;
  noAnswerAt: string | null;
}

/** Отметка звонка в карточке заведения. */
export interface CallView {
  id: string;
  result: CallResult;
  comment: string | null;
  createdAt: string;
  manager: { id: string | null; name: string };
  /** Своя отметка, 15 минут на «ошибся кнопкой». */
  canUndo: boolean;
}

export type CallPeriod = 'today' | '7d' | '30d';

export const CALL_PERIODS: Array<{ value: CallPeriod; label: string }> = [
  { value: 'today', label: 'Сегодня' },
  { value: '7d', label: '7 дней' },
  { value: '30d', label: '30 дней' },
];

export type CallBucket = 'answered' | 'no_answer' | 'none';

export const CALL_BUCKETS: Array<{ value: CallBucket; label: string }> = [
  { value: 'answered', label: 'Дозвонились' },
  { value: 'no_answer', label: 'Не ответили' },
  { value: 'none', label: 'Не звонили' },
];

export const RESULT_LABEL: Record<CallResult, string> = {
  ANSWERED: 'Дозвонились',
  NO_ANSWER: 'Не ответили',
};

export function parseCallPeriod(raw: string | null): CallPeriod {
  return CALL_PERIODS.some((p) => p.value === raw) ? (raw as CallPeriod) : 'today';
}

export function parseCallBucket(raw: string | null): CallBucket | null {
  return CALL_BUCKETS.some((b) => b.value === raw) ? (raw as CallBucket) : null;
}

/** Начало периода по часам телефона: «сегодня» — с полуночи, 7 дней — сегодня и шесть до него. */
export function callPeriodStart(period: CallPeriod, now = new Date()): number {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  if (period === '7d') d.setDate(d.getDate() - 6);
  if (period === '30d') d.setDate(d.getDate() - 29);
  return d.getTime();
}

/**
 * Группа заведения за период: дозвонились хоть раз — «Дозвонились» (даже если
 * потом не взяли трубку); иначе не ответили — «Не ответили»; иначе «Не звонили».
 */
export function callBucket(call: CallSummary | null, since: number): CallBucket {
  if (!call) return 'none';
  if (call.answeredAt && Date.parse(call.answeredAt) >= since) return 'answered';
  if (call.noAnswerAt && Date.parse(call.noAnswerAt) >= since) return 'no_answer';
  return 'none';
}

const TIME_FMT = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' });
const DAY_FMT = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' });

/** «сегодня 14:05», «вчера 18:20», «2 нояб.» — когда звонили. */
export function callWhen(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today.getTime() - new Date(d).setHours(0, 0, 0, 0)) / 86_400_000);
  if (diffDays <= 0) return `сегодня ${TIME_FMT.format(d)}`;
  if (diffDays === 1) return `вчера ${TIME_FMT.format(d)}`;
  return DAY_FMT.format(d);
}

export interface MarkResult {
  ok: true;
  call: CallView;
  summary: CallSummary | null;
}

export function markCall(establishmentId: string, result: CallResult, comment?: string): Promise<MarkResult> {
  return apiPost<MarkResult>(
    `/establishments/${establishmentId}/calls`,
    { result, ...(comment?.trim() ? { comment: comment.trim() } : {}) },
    { silent: true },
  );
}

export function undoCall(callId: string): Promise<{ ok: true; establishmentId: string; summary: CallSummary | null }> {
  return apiDelete(`/calls/${callId}`, { silent: true });
}
